import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '~/api/queryKeys';

import {
  getJobRunConfig,
  getJobRunConfigHistory,
  updateJobRunConfig,
} from '../services/scheduled-enrichment.service';
import type {
  JobConfigChangeEntry,
  JobRunConfig,
  UpdateJobRunConfigRequest,
} from '../types/job-run-config.types';

/** The scheduled job's stored run config — what the next trigger inherits. */
export function useJobRunConfig(jobId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.jobRunConfig(jobId),
    queryFn: () => getJobRunConfig(jobId),
    enabled: (options?.enabled ?? true) && Boolean(jobId),
    staleTime: 30_000,
  });
}

/** The job's run-config change history, newest first. Loaded when the history panel opens. */
export function useJobRunConfigHistory(jobId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.scheduledEnrichment.jobRunConfigHistory(jobId),
    queryFn: () => getJobRunConfigHistory(jobId),
    enabled: (options?.enabled ?? true) && Boolean(jobId),
  });
}

/**
 * Edits the job's run config. On success the response's `config` replaces the cached one —
 * the scheduled adapter mirrors that cache into the run-config store, so the next trigger
 * carries the edit instead of reverting it — and the recorded change is prepended to history
 * rather than refetched. Job status and the batch header echo the same config, so both refresh.
 */
export function useUpdateJobRunConfig(jobId: string, catalogBatchId?: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateJobRunConfigRequest) => updateJobRunConfig(jobId, payload),
    onSuccess: (res) => {
      queryClient.setQueryData<JobRunConfig>(
        queryKeys.scheduledEnrichment.jobRunConfig(jobId),
        res.config,
      );
      const change = res.change;
      if (res.changed && change) {
        queryClient.setQueryData<JobConfigChangeEntry[]>(
          queryKeys.scheduledEnrichment.jobRunConfigHistory(jobId),
          (prev) => (prev ? [change, ...prev.filter((e) => e.id !== change.id)] : prev),
        );
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.scheduledEnrichment.jobStatus(jobId),
      });
      if (catalogBatchId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.batchHeader(catalogBatchId),
        });
      }
    },
  });
}
